/**
 * ErrorBoundary — React class component that catches runtime errors (NP-46).
 *
 * Wraps children and renders the ErrorState component as a fallback
 * when an unhandled error occurs during rendering.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorState } from "@/components/ErrorState";
import { isChunkLoadError } from "@/lib/lazyWithReload";
import { LegalFooterLinks } from "@/components/LegalFooterLinks";

interface ErrorBoundaryProps {
  /** Content to render when no error has occurred */
  children: ReactNode;
  /** Optional fallback message override */
  fallbackMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * NP-442: for a stale-chunk failure, clearing boundary state is useless — React
   * re-attempts the SAME dead hashed URL and fails identically, so the button
   * labelled "Reload" never reloaded anything and the user was stuck.
   *
   * A real reload re-fetches index.html (served `no-store`) and therefore the new
   * asset manifest, which is what actually recovers. `lazyWithReload` already
   * does this automatically on the first failure; reaching this boundary means
   * that budget was spent, so this is the manual second chance.
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Build a descriptive message: include error type + message when available
      // so the user and support staff can identify what failed.
      const { error } = this.state;
      const specificMessage =
        this.props.fallbackMessage ??
        (error
          ? `[${error.name}] ${error.message}`
          : "Rendering failed — please refresh the page.");
      // A stale chunk is only recoverable by re-fetching the asset manifest, so
      // that case gets a real reload. Every other error keeps the existing soft
      // retry, which can survive a transient render fault without losing state.
      const chunkFailure = isChunkLoadError(error);
      // NP-582 AC-5: this replaces the entire tree, layouts included, so the legal
      // links have to come from here — there is no footer left to inherit.
      return (
        <div className="min-h-viewport flex flex-col">
          <div className="flex-1">
            <ErrorState
              message={specificMessage}
              retryLabel="Reload"
              onRetry={chunkFailure ? this.handleReload : this.handleRetry}
            />
          </div>
          <footer className="px-4 py-6">
            <LegalFooterLinks />
          </footer>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
